/**
 * Login screen — Google OAuth + one unified email/password form.
 *
 * The single form routes to:
 *   - `auth.localLogin` when the env exposes the dev local-login (any
 *     MOCK_USERS email + LOCAL_PASSWORD)
 *   - `auth.passwordLogin` otherwise (real DB-backed credentials)
 *
 * Below the form: links to "register" and "forgot password".
 */

import { useForm, useStore } from "@tanstack/react-form";
import { Link, useNavigate } from "@tanstack/react-router";

import { t } from "../i18n";
import { loginSchema } from "../lib/schemas";
import { getApiBase, trpc } from "../lib/trpc";
import { fieldError } from "../lib/validation";
import { AuthShell } from "./AuthShell";
import { GoogleIcon } from "./GoogleIcon";
import { PasswordInput } from "./PasswordInput";
import { Badge, Button, Field, FieldMessage, Input, Separator, Spinner, Stack } from "./ui";

export default function Login() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const methods = trpc.auth.loginMethods.useQuery();

  // After a successful login the user lands on whatever URL they were on
  // when the session expired. That's almost always a deep-linked settlement
  // page, which is confusing — they expect the household list. Force them
  // back to "/" so login always means "fresh start at the front page".
  const onLoginSuccess = () => {
    utils.auth.me.invalidate();
    navigate({ to: "/" });
  };

  const passwordLogin = trpc.auth.passwordLogin.useMutation({ onSuccess: onLoginSuccess });
  const localLogin = trpc.auth.localLogin.useMutation({ onSuccess: onLoginSuccess });

  const localEmails = methods.data?.localEmails ?? [];

  const form = useForm({
    defaultValues: { email: "", password: "" },
    validators: { onChange: loginSchema, onSubmit: loginSchema },
    onSubmit: ({ value }) => {
      const isMock = localEmails.includes(value.email.trim().toLowerCase());
      const mutation = isMock ? localLogin : passwordLogin;
      mutation.mutate({ email: value.email, password: value.password });
    },
  });

  const emailValue = useStore(form.store, (s) => s.values.email);
  const emailIsMock = localEmails.includes(emailValue.trim().toLowerCase());
  const activeLogin = emailIsMock ? localLogin : passwordLogin;

  if (methods.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="md" label={t.common.loading} />
      </div>
    );
  }

  return (
    <AuthShell>
      <Stack gap={4}>
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={() => {
            window.location.href = `${getApiBase()}/auth/google/start`;
          }}
        >
          <GoogleIcon size="sm" />
          {t.login.googleSignIn}
        </Button>

        <div className="flex items-center gap-3 text-ink-400 text-xs">
          <Separator className="flex-1" />
          {t.common.or}
          <Separator className="flex-1" />
        </div>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <form.Field name="email">
            {(field) => (
              <Field label={t.login.emailLabel} error={fieldError(field)}>
                <Input
                  type="email"
                  autoComplete="email"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={activeLogin.isPending}
                  iconLeft="mail"
                  placeholder={t.login.emailPlaceholder}
                  invalid={!!fieldError(field)}
                  suffix={
                    emailIsMock ? (
                      <Badge tone="warning" iconLeft="zap">
                        {t.login.devOnlyBadge}
                      </Badge>
                    ) : undefined
                  }
                />
              </Field>
            )}
          </form.Field>
          <form.Field name="password">
            {(field) => (
              <Field label={t.login.passwordLabel} error={fieldError(field)}>
                <PasswordInput
                  autoComplete="current-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={activeLogin.isPending}
                  placeholder={emailIsMock ? undefined : t.login.passwordPlaceholder}
                  invalid={!!fieldError(field)}
                />
              </Field>
            )}
          </form.Field>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            isLoading={activeLogin.isPending}
            disabled={activeLogin.isPending}
          >
            {t.login.submitButton}
          </Button>
          {activeLogin.isError && (
            <FieldMessage tone="error">{activeLogin.error.message}</FieldMessage>
          )}
          <div className="flex items-center justify-between pt-1 text-xs">
            <Link
              to="/forgot-password"
              className="text-accent-700 underline-offset-4 hover:text-accent-900 hover:underline dark:text-accent-300 dark:hover:text-accent-200"
            >
              {t.login.forgotPassword}
            </Link>
            <span className="text-ink-500">
              {t.login.noAccountPrompt}{" "}
              <Link
                to="/register"
                className="font-medium text-accent-700 underline-offset-4 hover:text-accent-900 hover:underline dark:text-accent-300 dark:hover:text-accent-200"
              >
                {t.login.registerLink}
              </Link>
            </span>
          </div>
        </form>

        {!methods.data?.local && !methods.data?.password && (
          <FieldMessage tone="error" className="text-sm">
            {t.login.noMethodsConfigured}
          </FieldMessage>
        )}
      </Stack>
    </AuthShell>
  );
}
